import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import makeStyles from '@mui/styles/makeStyles';
import { Suspense, useCallback, useEffect } from 'react';
import { Link, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';

import { calendarState } from '../../recoil/atoms/calendarState';
import { Role } from '../../recoil/atoms/user';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import { GenericSkeleton } from '../modules/Skeleton/GenericSkeleton';
import { UnauthorizedPage } from '../modules/Unauthorized/UnauthorizedPage';
import { AgentReports } from '../subpages/AgentReports';
import AgentStatus from '../subpages/AgentStatus';
import { AgentSummary } from '../subpages/AgentSummary';
import { BusinessReports } from '../subpages/BusinessReports';
import BusinessSnapshot from '../subpages/BusinessSnapshot';
import { Dispositions } from '../subpages/Dispositions';
import { InboundSummary } from '../subpages/InboundSummary';
import { QueuesLive } from '../subpages/QueuesLive';
import { QueuesReport } from '../subpages/QueuesReport';
import ScheduledReports from '../subpages/ScheduledReports';
import { SmsReports } from '../subpages/SmsReports';
import TimeSaved from '../subpages/TimeSaved';
import TransferReports from '../subpages/TransferReports';

const useStyles = makeStyles((theme) => ({
  tabs: {
    borderBottom:
      theme.palette.mode === 'dark' ? '1px solid #515151' : '1px solid #e0e0e0',
    indicator: {
      backgroundColor: '#0098C7',
    },
  },
  tab: {
    textTransform: 'none',
    minWidth: 72,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
    },
    '&[aria-selected=true]': {
      color: '#0098C7',
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  paper: {
    flexGrow: 1,
  },
}));

export function CallReportingTabs({ basePath = '/call-reporting' }) {
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role, canDownloadHistory } = permissionsLoadable?.contents ?? {};

  const businessSnapshotEnabled =
    process.env.VITE_APP_BUSINESS_SNAPSHOT === '1';
  const pages = {
    businessSnapshot: {
      pathname: `${basePath}/business-snapshot`,
    },
    agentSummary: {
      pathname: `${basePath}/agent-summary`,
    },
    agentReports: {
      pathname: `${basePath}/agent-reports`,
      showDuration: true,
    },
    agentStatus: {
      pathname: `${basePath}/agent-status`,
      showDuration: true,
    },
    smsReports: {
      pathname: `${basePath}/sms-reports`,
      showDuration: true,
    },
    businessReports: {
      pathname: `${basePath}/business-reports`,
      showDuration: true,
    },
    inboundSummary: {
      pathname: `${basePath}/inbound-summary`,
    },
    disposition: {
      pathname: `${basePath}/dispositions`,
    },
    timeSaved: {
      pathname: `${basePath}/time-saved`,
    },
    queues: {
      pathname: `${basePath}/queues`,
    },
    queuesLive: {
      pathname: `${basePath}/live-queues`,
    },
    scheduledReports: {
      pathname: `${basePath}/scheduled-reports`,
    },
    transferReports: {
      pathname: `${basePath}/transfer-reports`,
    },
  };
  // create lookup table [pathname]: boolean
  // const showDurationLookup = Object.values(pages)
  //   .filter((page) => page.showDuration)
  //   .reduce(
  //     (lookup, page) => ({
  //       ...lookup,
  //       [page.pathname]: true,
  //     }),
  //     {}
  //   );
  const [, setCalendarDisplay] = useRecoilState(calendarState);
  const classes = useStyles();
  const location = useLocation();

  // useCallback to prevent rerendering on same input
  const handleChange = useCallback(
    (event) => {
      // using getAttribute since href by itself will return absolute path
      // const destination = event.currentTarget
      //   .getAttribute('href')
      //   .split('?')[0];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    setCalendarDisplay('block');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canViewScheduledReportsCheck = () =>
    Number(process.env.VITE_APP_SCHEDULED_REPORTS) === 1 && canDownloadHistory;

  return (
    <Paper className={classes.paper}>
      {location.pathname !== basePath &&
        location.pathname !== `${basePath}/` && ( // on initial load prevent render since basePath tab undefined
          <Box display="flex" justifyContent="center" width="100%">
            <Tabs
              className={classes.tabs}
              value={location.pathname}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
            >
              <Tab
                className={classes.tab}
                label="Agent Reports"
                disableRipple
                component={Link}
                value={pages.agentReports.pathname}
                to={{
                  pathname: pages.agentReports.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
              {process.env.VITE_APP_AGENT_STATUS === `1` && (
                <Tab
                  className={classes.tab}
                  label="Agent Status"
                  disableRipple
                  component={Link}
                  value={pages.agentStatus.pathname}
                  to={{
                    pathname: pages.agentStatus.pathname,
                    search: location.search,
                  }}
                  onClick={handleChange}
                />
              )}
              <Tab
                className={classes.tab}
                label="Agent Summary"
                disableRipple
                component={Link}
                value={pages.agentSummary.pathname}
                to={{
                  pathname: pages.agentSummary.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
              <Tab
                className={classes.tab}
                label="Business Reports"
                disableRipple
                component={Link}
                value={pages.businessReports.pathname}
                to={{
                  pathname: pages.businessReports.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
              {businessSnapshotEnabled && (
                <Tab
                  className={classes.tab}
                  label="Business Snapshot"
                  disableRipple
                  component={Link}
                  value={pages.businessSnapshot.pathname}
                  to={{
                    pathname: pages.businessSnapshot.pathname,
                    search: location.search,
                  }}
                  onClick={handleChange}
                />
              )}
              <Tab
                className={classes.tab}
                label="Call Outcomes"
                disableRipple
                component={Link}
                value={pages.disposition.pathname}
                to={{
                  pathname: pages.disposition.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
              <Tab
                className={classes.tab}
                label="Inbound Summary"
                disableRipple
                component={Link}
                value={pages.inboundSummary.pathname}
                to={{
                  pathname: pages.inboundSummary.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
              <Tab
                className={classes.tab}
                label="Queues Reports"
                disableRipple
                component={Link}
                value={pages.queues.pathname}
                to={{
                  pathname: pages.queues.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
              {/* <Tab
                className={classes.tab}
                label="Queues Live"
                disableRipple
                component={Link}
                value={pages.queuesLive.pathname}
                to={{
                  pathname: pages.queuesLive.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              /> */}
              {process.env.VITE_APP_SCHEDULED_REPORTS === `1` &&
                role !== Role.USER && (
                  <Tab
                    className={classes.tab}
                    label="Scheduled Reports"
                    disableRipple
                    component={Link}
                    value={pages.scheduledReports.pathname}
                    to={{
                      pathname: pages.scheduledReports.pathname,
                      search: location.search,
                    }}
                    onClick={handleChange}
                  />
                )}
              <Tab
                className={classes.tab}
                label="SMS Reports"
                disableRipple
                component={Link}
                value={pages.smsReports.pathname}
                to={{
                  pathname: pages.smsReports.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
              <Tab
                className={classes.tab}
                label="Time Saved"
                disableRipple
                component={Link}
                value={pages.timeSaved.pathname}
                to={{
                  pathname: pages.timeSaved.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
              <Tab
                className={classes.tab}
                label="Transfer Reports"
                disableRipple
                component={Link}
                value={pages.transferReports.pathname}
                to={{
                  pathname: pages.transferReports.pathname,
                  search: location.search,
                }}
                onClick={handleChange}
              />
            </Tabs>
          </Box>
        )}
      <Suspense fallback={<GenericSkeleton />}>
        <Switch>
          <Route
            path={pages.agentReports.pathname}
            render={(props) => <AgentReports {...props} />}
          />
          <Redirect
            exact
            from={basePath}
            to={{
              pathname: pages.agentSummary.pathname,
              search: location.search,
            }}
          />
          {process.env.VITE_APP_AGENT_STATUS === `1` && (
            <Route
              path={pages.agentStatus.pathname}
              render={(props) => <AgentStatus {...props} />}
            />
          )}
          <Route
            path={pages.agentSummary.pathname}
            render={(props) => <AgentSummary {...props} />}
          />
          <Route
            path={pages.businessReports.pathname}
            render={() => <BusinessReports />}
          />
          {businessSnapshotEnabled && (
            <Route
              path={pages.businessSnapshot.pathname}
              render={(props) => <BusinessSnapshot {...props} />}
            />
          )}
          <Route
            path={pages.disposition.pathname}
            render={() => <Dispositions />}
          />
          <Route
            path={pages.inboundSummary.pathname}
            render={() => <InboundSummary />}
          />
          <Route
            path={pages.smsReports.pathname}
            render={() => <SmsReports />}
          />
          {process.env.VITE_APP_SCHEDULED_REPORTS === `1` && (
            <Route
              path={pages.scheduledReports.pathname}
              render={() => {
                // Wait for permissions to load before checking
                // Show loading for both 'loading' and 'hasError' states (error happens on refresh when user atom not yet hydrated)
                if (permissionsLoadable.state !== 'hasValue') {
                  return <GenericSkeleton />;
                }
                return canViewScheduledReportsCheck() ? (
                  <ScheduledReports />
                ) : (
                  <Redirect to="/unauthorized" />
                );
              }}
            />
          )}
          <Route path={pages.queues.pathname} render={() => <QueuesReport />} />
          <Route
            path={pages.queuesLive.pathname}
            render={() => <QueuesLive />}
          />
          <Route path={pages.timeSaved.pathname} render={() => <TimeSaved />} />
          <Route
            path={pages.transferReports.pathname}
            render={() => <TransferReports />}
          />
          <Route component={UnauthorizedPage} path="*" />
        </Switch>
      </Suspense>
    </Paper>
  );
}
